/**
 * Split free text into plain runs and web links, for announcements.
 *
 * Announcement title and body are stored unsanitised (see the note in
 * NotificationBell), so the output is data for JSX to render: text segments
 * stay text, and only a segment that parses as an absolute http(s) URL becomes
 * a link. Nothing here produces HTML, so there is no markup to inject.
 *
 * Only an explicit `http://` or `https://` prefix counts. A bare "example.com"
 * stays text: guessing at hosts turns file names and version numbers into
 * links, and `javascript:` or `data:` can never match because the scheme is
 * part of the pattern and re-checked after parsing.
 */

export type TextSegment =
  { kind: "text"; text: string } | { kind: "link"; text: string; href: string };

// Stops at whitespace and at characters that cannot appear unescaped in a URL
// but commonly wrap one in prose: <, >, quotes.
const URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;

// Punctuation that usually ends the sentence rather than the URL: "see
// https://x.com/survey." or "(https://x.com/survey)".
const TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/;

function safeHref(candidate: string): string | null {
  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (!url.hostname) return null;
    return url.href;
  } catch {
    return null;
  }
}

export function linkify(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let cursor = 0;
  const pushText = (value: string) => {
    if (!value) return;
    const last = segments.at(-1);
    if (last?.kind === "text") last.text += value;
    else segments.push({ kind: "text", text: value });
  };

  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index;
    let raw = match[0];
    const trailing = TRAILING_PUNCTUATION.exec(raw)?.[0] ?? "";
    // A closing parenthesis belongs to the URL when the URL opened one, as in
    // Wikipedia-style addresses.
    let keep = trailing;
    if (keep.startsWith(")") && raw.includes("(")) keep = "";
    raw = raw.slice(0, raw.length - keep.length);

    pushText(text.slice(cursor, start));
    const href = safeHref(raw);
    if (href === null) pushText(raw);
    else segments.push({ kind: "link", text: raw, href });
    cursor = start + raw.length;
  }
  pushText(text.slice(cursor));
  return segments;
}

/** The distinct links in a text, in order of appearance. */
export function linksIn(text: string): string[] {
  const seen = new Set<string>();
  for (const segment of linkify(text)) {
    if (segment.kind === "link") seen.add(segment.href);
  }
  return [...seen];
}
